import React, { createContext, useContext, useEffect, useState, useCallback, startTransition } from 'react';
import {
    User,
    onAuthStateChanged,
    signInWithPopup,
    signInWithRedirect,
    getRedirectResult,
    GoogleAuthProvider,
    sendSignInLinkToEmail,
    isSignInWithEmailLink,
    signInWithEmailLink,
    signOut as firebaseSignOut
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../firebase';
import { PendingAction, UserProfile } from '../types';
import { toast } from '../src/lib/toast';
import { safeStorage } from '../lib/safeStorage';

interface AuthContextType {
    user: User | null;
    userProfile: UserProfile | null;
    isLoading: boolean;
    isAuthModalOpen: boolean;
    pendingAction: PendingAction | null;
    openAuthModal: (action?: PendingAction) => void;
    closeAuthModal: () => void;
    signInWithGoogle: () => Promise<User | null>;
    signInWithGoogleRedirect: () => Promise<void>;
    sendMagicLink: (email: string) => Promise<void>;
    signOut: (options?: { silent?: boolean }) => Promise<void>;
    clearPendingAction: () => void;
    // Cross-device magic-link: true when a valid sign-in link was opened on a
    // device/browser that has no stored email, so the user must re-enter it to finish.
    magicLinkEmailNeeded: boolean;
    completeMagicLinkSignIn: (email: string) => Promise<void>;
    dismissMagicLinkPrompt: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const PENDING_ACTION_KEY = 'pendingAuthAction';
const EMAIL_FOR_SIGN_IN_KEY = 'emailForSignIn';
// How long (ms) after a magic-link sign-in to keep swallowing transient signed-out
// blips. Real Firebase emits a brief user→null→user token blip just after
// signInWithEmailLink; this window absorbs it. Comfortably longer than the blip,
// short enough that a genuine later sign-out is still handled.
const MAGIC_LINK_SETTLE_MS = 4000;

// Build the app's UserProfile from a Firebase user — shared by the auth-state
// listener and the cross-tab reconciler so they can't drift.
const buildUserProfile = (fbUser: User): UserProfile => ({
    uid: fbUser.uid,
    email: fbUser.email || '',
    displayName: fbUser.displayName || undefined,
    photoURL: fbUser.photoURL || undefined,
    provider: fbUser.providerData[0]?.providerId === 'google.com' ? 'google' : 'email',
    createdAt: fbUser.metadata.creationTime || new Date().toISOString(),
});

// Redundant local functions initialization removed - using centralized functions from ../firebase

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
    const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
    // Holds a valid sign-in link URL when it was opened without a stored email
    // (cross-device / cleared storage) — drives the "confirm your email" prompt.
    const [magicLinkPendingUrl, setMagicLinkPendingUrl] = useState<string | null>(null);

    const isInitialAuthCheckDone = React.useRef(false);
    const isMagicLinkSignInActive = React.useRef(false);
    // Timestamp (ms) until which transient signed-out fires are treated as magic-link
    // token blips and swallowed (see MAGIC_LINK_SETTLE_MS).
    const magicLinkSettleUntil = React.useRef(0);
    // Safety net: the uid we've already greeted with a sign-in toast this session.
    // Cleared only on an explicit sign-out, so a listener blip can't re-toast it.
    const lastToastedUid = React.useRef<string | null>(null);
    const userRef = React.useRef<User | null>(null);
    const tabId = React.useRef(Math.random().toString(36).substring(7));
    // Fix #3: single shared BroadcastChannel instance — prevents duplicate listeners
    const authChannelRef = React.useRef<BroadcastChannel | null>(null);

    // Sync user state to ref for use in listeners
    useEffect(() => {
        userRef.current = user;
    }, [user]);

    // Cross-tab auth synchronization using BroadcastChannel
    // Fix #3: owns the single shared channel instance via authChannelRef
    useEffect(() => {
        if (typeof window === 'undefined') return;
        // BroadcastChannel is missing in some embedded webviews (e.g. the ChatGPT
        // iOS in-app browser) and iOS Lockdown Mode. Cross-tab sync is a
        // nice-to-have — those contexts are single-tab anyway, so skip silently
        // and the ?.postMessage send sites below no-op. (JAVASCRIPT-REACT-A)
        if (typeof BroadcastChannel === 'undefined') return;
        const authChannel = new BroadcastChannel('auth_sync');
        authChannelRef.current = authChannel;

        authChannel.onmessage = (event) => {
            const data = event.data;
            if (data.sourceTabId === tabId.current) return;

            // A sign-in/out happened in another tab. We ACT ON THE MESSAGE directly
            // rather than reading auth.currentUser (whose cross-tab propagation is exactly
            // what's unreliable). Sign-out is the security-critical direction and is
            // handled without a reload; sign-in reloads only if this tab is still showing
            // signed-out. Broadcasts never fire on initial launch, so neither path loops
            // (the old blanket window.location.reload() DID loop — hence the guards).
            if (data.type === 'signOut' && userRef.current) {
                // Re-verify with the SDK before destroying anything.
                //
                // This branch used to act on the broadcast alone, because SDK cross-tab
                // propagation of auth.currentUser was unreliable (see the note above).
                // That unreliability was CAUSED by the forced setPersistence in
                // firebase.ts, which left the storage listener bound to IndexedDB while
                // the session data sat in localStorage; it was removed 2026-08-01. With
                // listener and data reunited, a genuine sign-out also arrives natively
                // via onAuthStateChanged, so this broadcast is a fast path rather than
                // the sole mechanism — and acting on a STALE one would delete a live
                // session from disk for every other tab.
                //
                // So: only act once the SDK agrees the session is gone. This makes the
                // handler corrective (sync React state to SDK truth) instead of
                // destructive. On a genuine sign-out where the SDK has not yet caught
                // up, we skip and its own listener completes the job within its poll
                // interval — marginally slower, and it cannot destroy a real session.
                //
                // KNOWN LIMIT: the two lowest persistence tiers (browserSession, inMemory,
                // selected only when BOTH IndexedDB and localStorage are unavailable) have
                // no-op _addListener, so nothing propagates and this tab keeps its session
                // until reload. That is acceptable because those tiers are per-tab stores
                // anyway — the tabs never shared a session for a sign-out to propagate
                // across. A timed re-check was considered and rejected: it cannot tell a
                // genuine sign-out from a stale broadcast in exactly those tiers (the SDK
                // reports a live user in both), so it would reinstate the destructive
                // behaviour this guard exists to remove.
                //
                // Clear the greeted-uid marker BEFORE that guard: it has to happen on every
                // received signOut, not only the ones we act on. Gated behind the guard, a
                // user who signs out in tab A and signs back in as the SAME uid in tab B
                // (popup flow, no reload) would get no "signed in" toast and tab B's signIn
                // broadcast would be dropped, because alreadyAcknowledged would still hold.
                lastToastedUid.current = null; // allow the next real sign-in to toast again
                if (auth.currentUser) return;
                // The shared session is gone → clear this tab's stale one. Its own
                // onAuthStateChanged(null) then updates the header. Silent (the acting
                // tab already showed the "signed out" toast).
                firebaseSignOut(auth).catch(() => { /* already signed out */ });
            } else if (data.type === 'signIn' && !userRef.current) {
                // Another tab signed in and this one still shows signed-out → reload to
                // adopt the shared session (the User object can't be reconstructed from a
                // uid alone).
                window.location.reload();
            }
        };

        return () => {
            authChannel.close();
            authChannelRef.current = null;
        };
    }, []);

    // Load pending action from storage on mount
    useEffect(() => {
        if (typeof window === 'undefined') return;
        const stored = safeStorage.getItem(PENDING_ACTION_KEY);
        if (stored) {
            try {
                setPendingAction(JSON.parse(stored));
            } catch {
                safeStorage.removeItem(PENDING_ACTION_KEY);
            }
        }
    }, []);

    // Handle magic link sign-in on page load
    useEffect(() => {
        if (typeof window === 'undefined') return;
        const handleMagicLink = async () => {
            // Check sessionStorage first (URL was preserved by early cleanup script in index.html)
            const preservedUrl = safeStorage.getItem('magicLinkUrl', 'session');
            const signInUrl = preservedUrl || window.location.href;

            if (isSignInWithEmailLink(auth, signInUrl)) {
                // Clean up the preserved URL IMMEDIATELY to prevent re-processing
                safeStorage.removeItem('magicLinkUrl', 'session');

                // Clean URL in browser. In sandboxed / opaque-origin contexts
                // replaceState itself throws SecurityError; signInUrl was already
                // captured above, so the sign-in proceeds without the cleanup
                // rather than dying here. (JAVASCRIPT-REACT-B follow-up)
                try {
                    window.history.replaceState(null, '', window.location.pathname);
                } catch {
                    // Credentials stay in the address bar for this load. The link
                    // is single-use and is consumed by the sign-in below.
                }

                if (isMagicLinkSignInActive.current) return;

                const email = safeStorage.getItem(EMAIL_FOR_SIGN_IN_KEY);
                if (email) {
                    isMagicLinkSignInActive.current = true;
                    magicLinkSettleUntil.current = Date.now() + MAGIC_LINK_SETTLE_MS;
                    // Remove email IMMEDIATELY
                    safeStorage.removeItem(EMAIL_FOR_SIGN_IN_KEY);

                    try {
                        // Wait for onAuthStateChanged to finish its first check
                        // This prevents flickering if a session already exists
                        let checkCount = 0;
                        while (!isInitialAuthCheckDone.current && checkCount < 10) {
                            await new Promise(r => setTimeout(r, 100));
                            checkCount++;
                        }

                        // If user is already logged in with this email, skip re-auth
                        if (auth.currentUser?.email === email) {
                            return;
                        }

                        await signInWithEmailLink(auth, email, signInUrl);
                    } catch (error) {
                        console.error('Magic link sign-in error:', error);
                        toast.error("This sign-in link has expired or was already used — please request a new one.");
                    } finally {
                        isMagicLinkSignInActive.current = false;
                        setIsLoading(false);
                    }
                } else {
                    // Cross-device / cleared storage: the link is valid but this device
                    // has no stored email to complete it. Prompt for it instead of
                    // failing silently (Firebase's recommended fallback).
                    setMagicLinkPendingUrl(signInUrl);
                    setIsLoading(false);
                }
            }
        };

        // handleMagicLink is async: without this catch a rejection becomes an
        // unhandled promise rejection, which the Sentry ErrorBoundary cannot
        // catch, so the sign-in would die with no toast and no visible cause.
        // isLoading is deliberately left to the auth-state listener — clearing
        // it here would race a still-in-flight session check.
        handleMagicLink().catch((error) => {
            console.error('Magic link handler failed:', error);
            toast.error("Couldn't complete sign-in from that link — please request a new one.");
        });
    }, []);

    // Auth state listener — deferred to avoid state updates during SSG hydration
    useEffect(() => {
        if (typeof window === 'undefined') return;
        // Fix #3: use the shared channel instance owned by the cross-tab sync effect above
        // (no new BroadcastChannel here — eliminates the duplicate listener)

        // Defer Firebase auth until the browser is idle so it never fires during
        // React's hydration pass. requestIdleCallback is the most reliable signal
        // that React has finished its first paint; setTimeout(200) is the Safari fallback.
        let unsubscribe: (() => void) | null = null;
        let timerId: ReturnType<typeof setTimeout> | null = null;

        const startAuthListener = () => {
            // Complete any pending Google-redirect sign-in (the popup-blocked fallback)
            // and surface its errors — previously getRedirectResult was never read, so a
            // failed redirect vanished silently. Success flows through onAuthStateChanged.
            getRedirectResult(auth).catch((error) => {
                console.error('Google redirect sign-in error:', error);
                toast.error("Google sign-in didn't complete — please try again.");
            });

            unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
                // Swallow transient signed-out blips while a magic-link sign-in is in
                // flight OR still settling. Real Firebase emits a brief user→null→user
                // token blip just after signInWithEmailLink; without this window that null
                // is misread as a sign-out — header flash, duplicate sign-in toast, and a
                // spurious cross-tab sign-out broadcast (all three were reproduced).
                if (!firebaseUser && (isMagicLinkSignInActive.current || Date.now() < magicLinkSettleUntil.current)) {
                    return;
                }

                const prevUser = userRef.current;
                // Update ref IMMEDIATELY (outside startTransition) to prevent race
                // conditions with BroadcastChannel before state settles
                userRef.current = firebaseUser;

                // Keep the magic-link settle window alive around the actual sign-in, so a
                // token blip arriving just AFTER the user appears is still swallowed above.
                if (firebaseUser && (isMagicLinkSignInActive.current || Date.now() < magicLinkSettleUntil.current)) {
                    magicLinkSettleUntil.current = Date.now() + MAGIC_LINK_SETTLE_MS;
                }

                // Fix #2: wrap all state updates in startTransition so React treats
                // them as non-urgent — they cannot interrupt an in-progress hydration pass
                startTransition(() => {
                    setUser(firebaseUser);

                    if (firebaseUser) {
                        setUserProfile(buildUserProfile(firebaseUser));
                    } else {
                        setUserProfile(null);
                    }

                    setIsLoading(false);
                });

                if (firebaseUser) {
                    // Safety net (2026-07-06): track the uid we've acknowledged as signed
                    // in. Cleared only on an explicit sign-out (signOut() / cross-tab),
                    // never on a listener null fire — so a transient signed-out blip that
                    // slips past the settle window still can't duplicate the toast/broadcast.
                    const alreadyAcknowledged = lastToastedUid.current === firebaseUser.uid;
                    lastToastedUid.current = firebaseUser.uid;

                    // Call ensureUserDocument to create/update user doc in Firestore (non-blocking)
                    httpsCallable(functions, 'ensureUserDocument')().catch(error => {
                        console.warn('Failed to ensure user document:', error);
                    });

                    // Show the sign-in toast + notify other tabs — but only for a genuine
                    // new sign-in: the initial check has completed OR a magic link is active
                    // (never a silent session restore), AND we haven't already greeted this uid.
                    if ((isInitialAuthCheckDone.current || isMagicLinkSignInActive.current) && !alreadyAcknowledged) {
                        // Show sign-in toast on the acting tab. toast.success is not
                        // React state, so no startTransition wrapping is needed here.
                        toast.success("You've successfully signed in");
                        authChannelRef.current?.postMessage({
                            type: 'signIn',
                            uid: firebaseUser.uid,
                            sourceTabId: tabId.current
                        });
                    }
                } else {
                    userRef.current = null; // Sync update for sign out to prevent race conditions

                    // Broadcast sign-out ONLY if the initial check has already completed
                    if (isInitialAuthCheckDone.current && prevUser) {
                        authChannelRef.current?.postMessage({
                            type: 'signOut',
                            sourceTabId: tabId.current
                        });
                    }
                }

                isInitialAuthCheckDone.current = true;
            });
        };

        // Wait until the browser is idle (hydration complete) before initialising
        // Firebase auth. requestIdleCallback guarantees React has finished its first
        // paint; the setTimeout fallback covers Safari which lacks rIC support.
        if (typeof requestIdleCallback !== 'undefined') {
            const rICHandle = requestIdleCallback(startAuthListener);
            return () => {
                cancelIdleCallback(rICHandle);
                unsubscribe?.();
            };
        } else {
            timerId = setTimeout(startAuthListener, 200);
            return () => {
                if (timerId !== null) clearTimeout(timerId);
                unsubscribe?.();
            };
        }
    }, []);



    const openAuthModal = useCallback((action?: PendingAction) => {
        if (action) {
            setPendingAction(action);
            safeStorage.setItem(PENDING_ACTION_KEY, JSON.stringify(action));
        }
        setIsAuthModalOpen(true);
    }, []);

    const closeAuthModal = useCallback(() => {
        setIsAuthModalOpen(false);
    }, []);

    const clearPendingAction = useCallback(() => {
        setPendingAction(null);
        safeStorage.removeItem(PENDING_ACTION_KEY);
    }, []);

    const signInWithGoogle = useCallback(async (): Promise<User | null> => {
        const provider = new GoogleAuthProvider();
        provider.addScope('profile');
        provider.addScope('email');

        try {
            console.debug('[AuthContext] Initiating Google Sign-in with popup...');
            const result = await signInWithPopup(auth, provider);
            console.debug('[AuthContext] Google Sign-in successful:', result.user.uid);
            closeAuthModal();
            return result.user;
        } catch (error: any) {
            console.error('[AuthContext] Google sign-in error:', error);
            throw error;
        }
    }, [closeAuthModal]);

    const signInWithGoogleRedirect = useCallback(async (): Promise<void> => {
        const provider = new GoogleAuthProvider();
        provider.addScope('profile');
        provider.addScope('email');

        try {
            console.debug('[AuthContext] Initiating Google Sign-in with redirect...');
            await signInWithRedirect(auth, provider);
        } catch (error: any) {
            console.error('[AuthContext] Google sign-in redirect error:', error);
            throw error;
        }
    }, []);

    const sendMagicLink = useCallback(async (email: string) => {
        // Pin the continue URL to the canonical production origin rather than deriving
        // it from whatever origin loaded the page (defence-in-depth); live origin only
        // in dev/emulator.
        const continueOrigin = import.meta.env.DEV ? window.location.origin : 'https://martialartnetwork.com.au';
        const actionCodeSettings = {
            url: continueOrigin + '/auth',
            handleCodeInApp: true
        };

        try {
            await sendSignInLinkToEmail(auth, email, actionCodeSettings);
            safeStorage.setItem(EMAIL_FOR_SIGN_IN_KEY, email);
        } catch (error) {
            console.error('Magic link error:', error);
            throw error;
        }
    }, []);

    // Cross-device fallback: complete a pending sign-in link with a manually-entered email.
    const completeMagicLinkSignIn = useCallback(async (email: string) => {
        if (!magicLinkPendingUrl) return;
        isMagicLinkSignInActive.current = true;
        magicLinkSettleUntil.current = Date.now() + MAGIC_LINK_SETTLE_MS;
        try {
            await signInWithEmailLink(auth, email, magicLinkPendingUrl);
            setMagicLinkPendingUrl(null);
        } catch (error) {
            console.error('Magic link (manual email) sign-in error:', error);
            toast.error("Couldn't sign you in — check the email address or request a new link.");
            throw error;
        } finally {
            isMagicLinkSignInActive.current = false;
        }
    }, [magicLinkPendingUrl]);

    const dismissMagicLinkPrompt = useCallback(() => {
        setMagicLinkPendingUrl(null);
    }, []);

    const signOut = useCallback(async (options?: { silent?: boolean }) => {
        try {
            await firebaseSignOut(auth);
            lastToastedUid.current = null; // allow the next real sign-in to toast again
            clearPendingAction();
            safeStorage.removeItem(EMAIL_FOR_SIGN_IN_KEY); // hygiene: no stale pending-email
            // Sign-out toast fires on the acting tab only, and only for
            // USER-INITIATED sign-outs. Programmatic callers (e.g. the
            // account-deletion flow, which calls signOut as an internal
            // sub-step before firing its own deletion toast) pass
            // { silent: true } to suppress this toast.
            //
            // The OTHER tab already reloads via the cross-tab broadcast
            // above; adding a cross-tab sign-out toast would require a new
            // sessionStorage key + a new post-reload check, which is
            // deferred — out of scope for this turn.
            if (!options?.silent) {
                toast.info("You've been signed out");
            }
        } catch (error) {
            console.error('Sign out error:', error);
            throw error;
        }
    }, [clearPendingAction]);

    const authContextValue = React.useMemo(() => ({
        user,
        userProfile,
        isLoading,
        isAuthModalOpen,
        pendingAction,
        openAuthModal,
        closeAuthModal,
        signInWithGoogle,
        signInWithGoogleRedirect,
        sendMagicLink,
        signOut,
        clearPendingAction,
        magicLinkEmailNeeded: magicLinkPendingUrl !== null,
        completeMagicLinkSignIn,
        dismissMagicLinkPrompt
    }), [
        user,
        userProfile,
        isLoading,
        isAuthModalOpen,
        pendingAction,
        openAuthModal,
        closeAuthModal,
        signInWithGoogle,
        signInWithGoogleRedirect,
        sendMagicLink,
        signOut,
        clearPendingAction,
        magicLinkPendingUrl,
        completeMagicLinkSignIn,
        dismissMagicLinkPrompt
    ]);

    return (
        <AuthContext.Provider value={authContextValue}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
